(function () {
  'use strict';

  var MONTH_DAYS = 30.4375;
  var DAYS_PER_WEEK = 7;
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var wholeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  var oneDecimal = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
  var dateFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  function toNumber(value) {
    var parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  function clamp(value, minimum, maximum) { return Math.min(Math.max(value, minimum), maximum); }

  function readInput(form, name, minimum, maximum) {
    var input = form.querySelector('[data-planner-input="' + name + '"]');
    return input ? clamp(toNumber(input.value), minimum, maximum) : NaN;
  }

  function hasInputValue(form, name) {
    var input = form.querySelector('[data-planner-input="' + name + '"]');
    return Boolean(input && input.value.trim() !== '');
  }

  function readOptionalInput(form, name, minimum, maximum, fallback) {
    return hasInputValue(form, name) ? readInput(form, name, minimum, maximum) : fallback;
  }

  function readChoice(form, name, fallback) {
    var input = form.querySelector('[data-planner-input="' + name + '"]');
    return input && input.value ? input.value : fallback;
  }

  function setText(root, name, value) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-planner-output="' + name + '"]'), function (node) { node.textContent = value; });
  }

  function setHidden(node, hidden) {
    if (!node) return;
    node.hidden = hidden;
    if (hidden) node.style.display = 'none';
    else node.style.removeProperty('display');
  }

  function validPositiveNumber(value) { return Number.isFinite(value) && value > 0; }
  function validNonNegativeNumber(value) { return Number.isFinite(value) && value >= 0; }
  function cleanString(value) { return typeof value === 'string' ? value.trim() : ''; }
  function validCurrentRecordStatus(status) { return status === 'real' || status === 'dynamic' || status === 'derived'; }

  function makeCurrencyFormatter(currency, perPair) {
    if (!/^[A-Z]{3}$/.test(currency)) return null;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: perPair ? 4 : 2
      });
    } catch (error) { return null; }
  }

  function isoToday() {
    var now = new Date();
    var pad = function (value) { return String(value).padStart(2, '0'); };
    return now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
  }

  function formatDate(iso) {
    if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return 'date beyond calendar display';
    var value = new Date(iso + 'T00:00:00Z');
    return Number.isFinite(value.getTime()) ? dateFormat.format(value) : 'date beyond calendar display';
  }

  function plural(value, singular, pluralValue) { return value === 1 ? singular : (pluralValue || singular + 's'); }

  function ordinal(value) {
    var rounded = Math.round(value);
    var remainder100 = rounded % 100;
    var suffix = 'th';
    if (remainder100 < 11 || remainder100 > 13) {
      if (rounded % 10 === 1) suffix = 'st';
      else if (rounded % 10 === 2) suffix = 'nd';
      else if (rounded % 10 === 3) suffix = 'rd';
    }
    return wholeNumber.format(rounded) + suffix;
  }

  function setMode(form) {
    var selected = form.querySelector('[data-planner-mode]:checked');
    var mode = selected ? selected.value : 'pairs';
    Array.prototype.forEach.call(form.querySelectorAll('[data-planner-mode-panel]'), function (panel) {
      var active = panel.getAttribute('data-planner-mode-panel') === mode;
      setHidden(panel, !active);
      Array.prototype.forEach.call(panel.querySelectorAll('input'), function (input) { input.disabled = !active; });
    });
    return mode;
  }

  function syncInboundState(form) {
    var inbound = form.querySelector('[data-planner-input="inboundPairs"]');
    var arrival = form.querySelector('[data-planner-input="inboundArrivalDays"]');
    if (inbound && arrival) arrival.disabled = !(toNumber(inbound.value) > 0);
  }

  function syncCurrentInventoryState(form) {
    var currentPairs = form.querySelector('[data-planner-input="currentPairs"]');
    var casesOnHand = form.querySelector('[data-planner-input="casesOnHand"]');
    if (!currentPairs || !casesOnHand) return false;
    var inventoryEntered = currentPairs.value.trim() !== '' || casesOnHand.value.trim() !== '';
    currentPairs.setCustomValidity(inventoryEntered ? '' : 'Enter current stock in pairs or cases. Enter 0 if no stock remains.');
    return inventoryEntered;
  }

  function withholdResults(root, results) {
    if (!results) return;
    results.hidden = true;
    results.removeAttribute('data-planner-scenario');
    var takeaway = root.querySelector('[data-planner-takeaway]');
    if (takeaway) takeaway.setAttribute('data-planner-takeaway-state', 'inventory-needed');
    setText(root, 'takeawayLead', 'Add your current inventory to turn the usage estimate into a reorder decision.');
    setText(root, 'takeawaySupport', 'The Planner can estimate demand and comparable-program behavior first, but it needs your on-hand stock to determine when to order.');
  }

  function readFacts(root) {
    return {
      minimumCases: toNumber(root.getAttribute('data-program-minimum-cases')),
      pairsPerCase: toNumber(root.getAttribute('data-program-pairs-per-case')),
      minimumPairs: toNumber(root.getAttribute('data-program-minimum-pairs')),
      productionDays: toNumber(root.getAttribute('data-program-production-days')),
      casePrice: toNumber(root.getAttribute('data-program-case-price')),
      setupOneColor: toNumber(root.getAttribute('data-program-setup-one-color')),
      setupTwoColor: toNumber(root.getAttribute('data-program-setup-two-color')),
      setupReorder: toNumber(root.getAttribute('data-program-setup-reorder')),
      currency: cleanString(root.getAttribute('data-program-currency')).toUpperCase(),
      casePriceStatus: cleanString(root.getAttribute('data-program-status-p011')),
      setupOneColorStatus: cleanString(root.getAttribute('data-program-status-p005')),
      setupTwoColorStatus: cleanString(root.getAttribute('data-program-status-p006')),
      setupReorderStatus: cleanString(root.getAttribute('data-program-status-p007')),
      productionStatus: cleanString(root.getAttribute('data-program-status-p016'))
    };
  }

  function readRecurringModel(root) {
    var node = root.querySelector('[data-ers-recurring-program-model]');
    if (!node) return null;
    try {
      var model = JSON.parse(node.textContent);
      return model && model.schemaVersion === '1.0.0' && Array.isArray(model.priorKnots) && model.priorKnots.length > 1 ? model : null;
    } catch (error) { return null; }
  }

  function programFactsAreValid(facts) {
    return validPositiveNumber(facts.minimumCases) && validPositiveNumber(facts.pairsPerCase) &&
      validPositiveNumber(facts.minimumPairs) && validNonNegativeNumber(facts.productionDays) &&
      validCurrentRecordStatus(facts.productionStatus) &&
      Math.abs((facts.minimumCases * facts.pairsPerCase) - facts.minimumPairs) < 0.01;
  }

  function selectedSetupRecord(form, facts) {
    var select = form.querySelector('[data-planner-setup]');
    if (!select || select.value === 'none') return null;
    if (select.value === 'one-color') return { label: 'New one-color artwork', formulaLabel: 'Current one-color setup charge', amount: facts.setupOneColor, status: facts.setupOneColorStatus };
    if (select.value === 'two-color') return { label: 'New two-color artwork', formulaLabel: 'Current two-color setup charge', amount: facts.setupTwoColor, status: facts.setupTwoColorStatus };
    if (select.value === 'reorder') return { label: 'Unchanged-artwork reorder', formulaLabel: 'Current unchanged-artwork reorder setup', amount: facts.setupReorder, status: facts.setupReorderStatus };
    return null;
  }

  function updateCostEstimate(root, form, facts, suggestedCases, suggestedPairs) {
    var panel = root.querySelector('[data-planner-cost]');
    var unavailable = root.querySelector('[data-planner-cost-unavailable]');
    var select = form.querySelector('[data-planner-setup]');
    var setup = selectedSetupRecord(form, facts);
    if (!panel || !unavailable || !select) return null;
    if (select.value === 'none') {
      setHidden(panel, true);
      setHidden(unavailable, true);
      return null;
    }
    var money = makeCurrencyFormatter(facts.currency, false);
    var perPairMoney = makeCurrencyFormatter(facts.currency, true);
    var baseValid = validPositiveNumber(facts.casePrice) && validCurrentRecordStatus(facts.casePriceStatus);
    var setupValid = setup && validNonNegativeNumber(setup.amount) && validCurrentRecordStatus(setup.status);
    if (!baseValid || !setupValid || !money || !perPairMoney || !validPositiveNumber(suggestedPairs)) {
      setHidden(panel, true);
      setHidden(unavailable, false);
      return null;
    }
    var merchandiseSubtotal = suggestedCases * facts.casePrice;
    var preFreightSubtotal = merchandiseSubtotal + setup.amount;
    var merchandisePerPair = merchandiseSubtotal / suggestedPairs;
    if (![merchandiseSubtotal, preFreightSubtotal, merchandisePerPair].every(Number.isFinite)) {
      setHidden(panel, true);
      setHidden(unavailable, false);
      return null;
    }
    setText(root, 'costLot', wholeNumber.format(suggestedCases) + ' ' + plural(suggestedCases, 'case') + ' · ' + wholeNumber.format(suggestedPairs) + ' pairs');
    setText(root, 'merchandiseSubtotal', money.format(merchandiseSubtotal));
    setText(root, 'setupAmount', money.format(setup.amount));
    setText(root, 'preFreightSubtotal', money.format(preFreightSubtotal));
    setText(root, 'merchandisePerPair', perPairMoney.format(merchandisePerPair));
    setText(root, 'setupTreatmentLabel', setup.label);
    setText(root, 'setupFormulaOwner', setup.formulaLabel);
    setHidden(panel, false);
    setHidden(unavailable, true);
    return { preFreightSubtotal: money.format(preFreightSubtotal) };
  }

  function svgNode(name, attributes, text) {
    var node = document.createElementNS(SVG_NS, name);
    Object.keys(attributes || {}).forEach(function (key) { node.setAttribute(key, String(attributes[key])); });
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function renderTrajectory(root, result) {
    var svg = root.querySelector('[data-planner-trajectory]');
    var list = root.querySelector('[data-planner-trajectory-events]');
    if (!svg || !list || !result.trajectory || !Array.isArray(result.trajectory.points)) return;
    var points = result.trajectory.points.slice();
    var hasDayZeroInbound = result.trajectory.events.some(function (event) {
      return event.type === 'existing-inbound-arrival' && event.day === 0;
    });
    if (hasDayZeroInbound && points.length && points[0].inventoryPairs !== result.conditions.currentInventoryPairs) {
      points.unshift({ day: 0, inventoryPairs: result.conditions.currentInventoryPairs, kind: 'current' });
    }
    var horizon = Math.max(1, result.trajectory.horizonDays);
    var maximum = Math.max(result.trajectory.protectionPairs, 1);
    points.forEach(function (point) { maximum = Math.max(maximum, point.inventoryPairs); });
    maximum *= 1.12;
    var left = 68;
    var right = 734;
    var top = 34;
    var bottom = 248;
    var x = function (day) { return left + day / horizon * (right - left); };
    var y = function (pairs) { return bottom - pairs / maximum * (bottom - top); };
    svg.replaceChildren();
    svg.appendChild(svgNode('rect', { x: left, y: y(result.trajectory.protectionPairs), width: right - left, height: bottom - y(result.trajectory.protectionPairs), class: 'ers-planner-trajectory__protection' }));
    svg.appendChild(svgNode('line', { x1: left, y1: bottom, x2: right, y2: bottom, class: 'ers-planner-trajectory__axis' }));
    svg.appendChild(svgNode('line', { x1: left, y1: top, x2: left, y2: bottom, class: 'ers-planner-trajectory__axis' }));
    svg.appendChild(svgNode('line', { x1: left, y1: y(result.trajectory.protectionPairs), x2: right, y2: y(result.trajectory.protectionPairs), class: 'ers-planner-trajectory__threshold' }));
    svg.appendChild(svgNode('text', { x: right - 4, y: y(result.trajectory.protectionPairs) - 7, 'text-anchor': 'end', class: 'ers-planner-trajectory__label' }, 'Protection ' + wholeNumber.format(result.trajectory.protectionPairs) + ' pairs'));
    svg.appendChild(svgNode('text', { x: left - 10, y: top + 4, 'text-anchor': 'end', class: 'ers-planner-trajectory__tick' }, wholeNumber.format(Math.round(maximum / 1.12))));
    svg.appendChild(svgNode('text', { x: left - 10, y: bottom + 4, 'text-anchor': 'end', class: 'ers-planner-trajectory__tick' }, '0'));
    svg.appendChild(svgNode('text', { x: left, y: bottom + 28, 'text-anchor': 'start', class: 'ers-planner-trajectory__tick' }, 'Today'));
    svg.appendChild(svgNode('text', { x: right, y: bottom + 28, 'text-anchor': 'end', class: 'ers-planner-trajectory__tick' }, 'Day ' + wholeNumber.format(horizon)));
    svg.appendChild(svgNode('polyline', { points: points.map(function (point) { return x(point.day) + ',' + y(point.inventoryPairs); }).join(' '), class: 'ers-planner-trajectory__line' }));

    var eventLabels = { 'reorder-decision': 'Reorder', 'existing-inbound-arrival': 'Inbound', 'recommended-replenishment-arrival': 'Replenishment' };
    result.trajectory.events.forEach(function (event, index) {
      var eventX = x(event.day);
      svg.appendChild(svgNode('line', { x1: eventX, y1: top, x2: eventX, y2: bottom, class: 'ers-planner-trajectory__event-line' }));
      svg.appendChild(svgNode('text', { x: eventX, y: 15 + (index % 2) * 13, 'text-anchor': eventX > right - 85 ? 'end' : eventX < left + 85 ? 'start' : 'middle', class: 'ers-planner-trajectory__event-label' }, eventLabels[event.type] || 'Event'));
    });
    points.forEach(function (point) {
      if (point.kind === 'before-event' || point.kind === 'projection') return;
      svg.appendChild(svgNode('circle', { cx: x(point.day), cy: y(point.inventoryPairs), r: 4.5, class: 'ers-planner-trajectory__point' }));
    });

    list.replaceChildren();
    var today = document.createElement('li');
    today.innerHTML = '<strong>Today</strong><span>' + wholeNumber.format(result.conditions.currentInventoryPairs) + ' pairs on hand</span>';
    list.appendChild(today);
    result.trajectory.events.forEach(function (event) {
      var item = document.createElement('li');
      var detail = formatDate(event.date);
      if (event.pairs) detail += ' · ' + wholeNumber.format(event.pairs) + ' pairs';
      item.innerHTML = '<strong>' + (eventLabels[event.type] || 'Inventory event') + '</strong><span>' + detail + ' · day ' + wholeNumber.format(event.day) + '</span>';
      list.appendChild(item);
    });
  }

  function supportPosition(comparable) {
    if (comparable.supportStatus === 'below-supported-range') return 'Below modeled support; nearest comparable prior used';
    if (comparable.supportStatus === 'above-supported-range') return 'Above modeled support; nearest comparable prior used';
    return 'About the ' + ordinal(comparable.usagePercentile) + ' percentile of modeled usage';
  }

  function renderResult(root, form, facts, result, mode, pairsPerOpenDay, daysPerWeek, locations, announce, isExample) {
    var comparable = result.comparable;
    var conditions = result.conditions;
    var adjustments = result.adjustments;
    var decision = result.decision;
    var resultSection = root.querySelector('[data-planner-results]');
    var actionLayer = root.querySelector('[data-planner-signal]');
    var takeaway = root.querySelector('[data-planner-takeaway]');
    var orderNow = decision.status === 'order-now';
    var signalShort = orderNow ? 'Order now' : 'Reorder in ' + wholeNumber.format(decision.reorderInDays) + ' days';
    var signalTiming = orderNow ? 'Safe reorder threshold reached' : 'By ' + formatDate(decision.reorderDate);

    setText(root, 'monthlyPairs', wholeNumber.format(conditions.baselineMonthlyPairs));
    setText(root, 'suggestedCases', wholeNumber.format(decision.recommendedCases) + ' ' + plural(decision.recommendedCases, 'case'));
    setText(root, 'suggestedPairsSummary', wholeNumber.format(decision.recommendedPairs) + ' pairs · current whole-case rounded');
    setText(root, 'signalShort', signalShort);
    setText(root, 'signalTiming', signalTiming);
    setText(root, 'comparablePosition', supportPosition(comparable));
    setText(root, 'comparableCoverageMedian', oneDecimal.format(comparable.coverageWeeks.median) + ' weeks typical');
    setText(root, 'comparableCoverageRange', oneDecimal.format(comparable.coverageWeeks.q25) + '–' + oneDecimal.format(comparable.coverageWeeks.q75) + ' weeks in the middle 50%');
    setText(root, 'comparableLot', wholeNumber.format(comparable.typicalLotPairs) + ' pairs typical');
    setText(root, 'comparableCadence', wholeNumber.format(comparable.cadenceDays) + ' days typical');
    var matchedNote = comparable.supportStatus === 'within-supported-range' ? '' : ' The comparable prior is bounded at ' + wholeNumber.format(comparable.matchedMonthlyPairs) + ' pairs per month; your actual projected usage remains in the operating calculation.';
    setText(root, 'comparableSummary', 'At ' + wholeNumber.format(conditions.baselineMonthlyPairs) + ' projected pairs per month, the local comparable summary uses ' + wholeNumber.format(comparable.distinctPrograms) + ' equally weighted recurring programs. Typical modeled coverage is ' + oneDecimal.format(comparable.coverageWeeks.median) + ' weeks, with a middle 50% from ' + oneDecimal.format(comparable.coverageWeeks.q25) + ' to ' + oneDecimal.format(comparable.coverageWeeks.q75) + ' weeks.' + matchedNote);

    setText(root, 'currentOnHandDays', oneDecimal.format(decision.currentOnHandDays) + ' days · ' + wholeNumber.format(conditions.currentInventoryPairs) + ' pairs');
    var demandDirection = conditions.seasonalChangePercent === 0 ? 'No future-demand change' : (conditions.seasonalChangePercent > 0 ? '+' : '') + oneDecimal.format(conditions.seasonalChangePercent) + '% future change';
    setText(root, 'futureDemandPath', demandDirection + ' · ' + wholeNumber.format(conditions.futureMonthlyPairs) + ' pairs/month');
    setText(root, 'effectiveLeadTime', wholeNumber.format(conditions.effectiveLeadDays) + ' days · ' + wholeNumber.format(conditions.productionDays) + ' production + ' + wholeNumber.format(conditions.transitDays) + ' transit');
    setText(root, 'protectionContext', wholeNumber.format(adjustments.protectionDays) + ' days · ' + wholeNumber.format(adjustments.protectionPairs) + ' pairs');
    var inboundText = conditions.inboundPairs > 0
      ? wholeNumber.format(conditions.inboundPairs) + ' inbound pairs arrive on day ' + wholeNumber.format(conditions.inboundArrivalDays) + '; ' + wholeNumber.format(adjustments.inboundPairsAppliedByReplenishment) + ' pairs are included in the projection by the recommended replenishment arrival.'
      : 'No existing inbound inventory was entered.';
    if (conditions.inboundPairs > 0 && adjustments.lateInboundPairsCreditedToLot > 0) {
      inboundText += ' Because the inbound stock arrives later, ' + wholeNumber.format(adjustments.lateInboundPairsCreditedToLot) + ' pairs are credited against replenishment need only after bridge protection through that arrival is maintained.';
    }
    setText(root, 'conditionSummary', 'The comparable baseline supplies normal cycle-stock behavior. Future demand is applied once, while ' + conditions.uncertainty + ' uncertainty changes only incremental protection. ' + inboundText);

    setText(root, 'signalHeading', orderNow ? 'Order now: the safe threshold has been reached.' : 'Reorder by ' + formatDate(decision.reorderDate) + '.');
    var beforeArrivalText = wholeNumber.format(decision.projectedInventoryBeforeArrivalPairs) + ' pairs';
    if (decision.uncoveredDemandBeforeArrivalPairs > 0) beforeArrivalText += ' · ' + wholeNumber.format(decision.uncoveredDemandBeforeArrivalPairs) + ' pairs of demand uncovered';
    setText(root, 'inventoryAtDecision', wholeNumber.format(decision.projectedInventoryAtDecisionPairs) + ' pairs');
    setText(root, 'inventoryBeforeArrival', beforeArrivalText);
    setText(root, 'inventoryAfterArrival', wholeNumber.format(decision.projectedInventoryAfterArrivalPairs) + ' pairs');
    setText(root, 'postArrivalCoverage', oneDecimal.format(decision.projectedPostArrivalCoverageDays) + ' days');
    setText(root, 'nextOrderExplanation', 'The ' + wholeNumber.format(decision.recommendedPairs) + '-pair lot is sized from projected inventory at the decision, carried through the ' + wholeNumber.format(conditions.effectiveLeadDays) + '-day effective lead time, then rounded to today\'s case pack and minimum. Expected arrival: ' + formatDate(decision.replenishmentArrivalDate) + '.');
    if (actionLayer) actionLayer.setAttribute('data-planner-signal', orderNow ? 'action' : 'monitor');
    if (takeaway) takeaway.setAttribute('data-planner-takeaway-state', orderNow ? 'order-now' : 'future-reorder');
    setText(root, 'takeawayLead', orderNow ? 'You\'ve already reached the reorder point.' : 'You still have room to wait before ordering.');
    setText(root, 'takeawaySupport', orderNow
      ? 'The recommendation is to order ' + wholeNumber.format(decision.recommendedCases) + ' ' + plural(decision.recommendedCases, 'case') + ' now based on your current stock, expected usage, lead time, and comparable ERS programs.'
      : 'At your current usage and inventory, your reorder point is about ' + wholeNumber.format(decision.reorderInDays) + ' days away; ordering ' + wholeNumber.format(decision.recommendedCases) + ' ' + plural(decision.recommendedCases, 'case') + ' then is projected to keep the next replenishment aligned with your inventory protection.');
    setText(root, 'trajectorySummary', 'From ' + wholeNumber.format(conditions.currentInventoryPairs) + ' pairs today, projected inventory reaches the reorder decision on ' + formatDate(decision.reorderDate) + ' and the recommended replenishment arrives on ' + formatDate(decision.replenishmentArrivalDate) + '.');
    setText(root, 'trajectoryDescription', 'Projected inventory from today through day ' + wholeNumber.format(result.trajectory.horizonDays) + '. Reorder decision on day ' + wholeNumber.format(decision.reorderInDays) + ', recommended replenishment arrival on day ' + wholeNumber.format(decision.replenishmentArrivalInDays) + ', and projected inventory after arrival of ' + wholeNumber.format(decision.projectedInventoryAfterArrivalPairs) + ' pairs.');
    renderTrajectory(root, result);

    var costSummary = updateCostEstimate(root, form, facts, decision.recommendedCases, decision.recommendedPairs);
    var intro = root.querySelector('[data-planner-result-intro]');
    if (intro) {
      var introPrefix = isExample ? 'Example scenario — ' : '';
      intro.textContent = introPrefix + (mode === 'covers'
        ? 'Based on ' + wholeNumber.format(readInput(form, 'coversPerDay', 1, 10000)) + ' covers per open day, ' + oneDecimal.format(readInput(form, 'chopstickShare', 1, 100)) + '% needing chopsticks, ' + wholeNumber.format(daysPerWeek) + ' days per week and ' + wholeNumber.format(locations) + ' ' + plural(locations, 'location') + '.'
        : 'Based on ' + wholeNumber.format(pairsPerOpenDay) + ' pairs per open day, ' + wholeNumber.format(daysPerWeek) + ' days per week and ' + wholeNumber.format(locations) + ' ' + plural(locations, 'location') + '.');
    }
    resultSection.hidden = false;
    resultSection.setAttribute('data-planner-scenario', isExample ? 'example' : 'user-edited');
    if (announce) {
      var liveSummary = root.querySelector('[data-planner-live-summary]');
      if (liveSummary) liveSummary.textContent = 'Plan updated. Baseline monthly need is ' + wholeNumber.format(conditions.baselineMonthlyPairs) + ' pairs. ' + signalShort + '. Recommended next lot is ' + wholeNumber.format(decision.recommendedCases) + ' ' + plural(decision.recommendedCases, 'case') + ', ' + wholeNumber.format(decision.recommendedPairs) + ' pairs.' + (costSummary ? ' Optional current pre-freight subtotal is ' + costSummary.preFreightSubtotal + '.' : '');
    }
  }

  function calculate(root, facts, model, announce, isExample) {
    var form = root.querySelector('[data-planner-form]');
    if (!form || !programFactsAreValid(facts) || !model || !window.ERSRecurringProgram) return;
    var mode = setMode(form);
    syncInboundState(form);
    if (!syncCurrentInventoryState(form)) {
      withholdResults(root, root.querySelector('[data-planner-results]'));
      return;
    }
    if (!form.checkValidity()) return;
    var pairsPerOpenDay = mode === 'covers'
      ? readInput(form, 'coversPerDay', 1, 10000) * (readInput(form, 'chopstickShare', 1, 100) / 100)
      : readInput(form, 'pairsPerDay', 1, 10000);
    var daysPerWeek = readInput(form, 'daysPerWeek', 1, 7);
    var locations = readInput(form, 'locations', 1, 1000);
    var currentPairs = readOptionalInput(form, 'currentPairs', 0, 100000000, 0);
    var casesOnHand = readOptionalInput(form, 'casesOnHand', 0, 100000, 0);
    var inboundPairs = readInput(form, 'inboundPairs', 0, 100000000);
    var inboundArrivalDays = readInput(form, 'inboundArrivalDays', 0, 365);
    var transitDays = readInput(form, 'transitDays', 0, 90);
    var seasonalChangePercent = readInput(form, 'seasonalChangePercent', -50, 100);
    var values = [pairsPerOpenDay, daysPerWeek, locations, currentPairs, casesOnHand, inboundPairs, transitDays, seasonalChangePercent];
    if (inboundPairs > 0) values.push(inboundArrivalDays);
    if (!values.every(Number.isFinite)) return;
    var monthlyPairs = pairsPerOpenDay * daysPerWeek * locations / DAYS_PER_WEEK * MONTH_DAYS;
    var inputs = {
      baselineMonthlyPairs: monthlyPairs,
      currentInventoryPairs: currentPairs + casesOnHand * facts.pairsPerCase,
      inboundPairs: inboundPairs,
      inboundArrivalDays: inboundPairs > 0 ? inboundArrivalDays : 0,
      transitDays: transitDays,
      seasonalChangePercent: seasonalChangePercent,
      uncertainty: readChoice(form, 'uncertainty', 'standard'),
      asOfDate: isoToday()
    };
    try {
      var result = window.ERSRecurringProgram.evaluate(model, inputs, {
        pairsPerCase: facts.pairsPerCase, minimumCases: facts.minimumCases, productionDays: facts.productionDays
      });
      var configurationError = root.querySelector('[data-planner-configuration-error]');
      if (configurationError) configurationError.hidden = true;
      renderResult(root, form, facts, result, mode, pairsPerOpenDay, daysPerWeek, locations, announce, isExample);
    } catch (error) {
      var configurationError = root.querySelector('[data-planner-configuration-error]');
      if (configurationError) {
        configurationError.textContent = 'The recurring-program model could not calculate this combination. Check the entered values and try again.';
        configurationError.hidden = false;
      }
    }
  }

  function initialize(root) {
    var form = root.querySelector('[data-planner-form]');
    var results = root.querySelector('[data-planner-results]');
    var facts = readFacts(root);
    var model = readRecurringModel(root);
    var configurationError = root.querySelector('[data-planner-configuration-error]');
    var calculateButton = root.querySelector('[data-planner-calculate]');
    var printButton = root.querySelector('[data-planner-print]');
    if (!form || !results) return;
    if (!programFactsAreValid(facts) || !model || !window.ERSRecurringProgram || typeof window.ERSRecurringProgram.evaluate !== 'function') {
      if (configurationError) {
        configurationError.textContent = 'Current ERS package information or the aggregate recurring-program model could not be loaded. Calculation is unavailable.';
        configurationError.hidden = false;
      }
      if (calculateButton) calculateButton.disabled = true;
      return;
    }
    setMode(form);
    syncInboundState(form);
    syncCurrentInventoryState(form);
    calculate(root, facts, model, false, true);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      syncCurrentInventoryState(form);
      if (!form.checkValidity()) { form.reportValidity(); return; }
      calculate(root, facts, model, true, false);
      results.focus();
    });
    Array.prototype.forEach.call(form.querySelectorAll('[data-planner-mode]'), function (radio) {
      radio.addEventListener('change', function () { setMode(form); if (form.checkValidity()) calculate(root, facts, model, false, false); });
    });
    var setupSelect = form.querySelector('[data-planner-setup]');
    if (setupSelect) setupSelect.addEventListener('change', function () { if (form.checkValidity()) calculate(root, facts, model, false, false); });
    var inputTimer;
    form.addEventListener('input', function () {
      syncInboundState(form);
      if (!syncCurrentInventoryState(form)) withholdResults(root, results);
      window.clearTimeout(inputTimer);
      inputTimer = window.setTimeout(function () { if (form.checkValidity()) calculate(root, facts, model, false, false); }, 160);
    });
    form.addEventListener('change', function (event) {
      if (event.target.matches('select[data-planner-input]') && form.checkValidity()) calculate(root, facts, model, false, false);
    });
    form.addEventListener('reset', function () {
      window.setTimeout(function () { setMode(form); syncInboundState(form); syncCurrentInventoryState(form); withholdResults(root, results); calculate(root, facts, model, true, true); }, 0);
    });
    if (printButton) printButton.addEventListener('click', function () { window.print(); });
  }

  function start() { Array.prototype.forEach.call(document.querySelectorAll('[data-ers-planner]'), initialize); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}());
